import React from "react";

const LAST_UPDATED = "April 15, 2026";
const CONTACT_EMAIL = "tamir@people-leads.com";
const SITE_URL = "https://momentum.people-leads.com";

function Section({ title, children }) {
  return (
    <section className="mb-8">
      <h2 className="text-xl font-bold text-white mb-3">{title}</h2>
      <div className="text-[rgba(255,255,255,0.7)] leading-relaxed space-y-3">
        {children}
      </div>
    </section>
  );
}

export default function PrivacyPolicy() {
  return (
    <div
      className="min-h-screen py-12 px-4"
      style={{ background: "#0a0a0a", fontFamily: "'Heebo', sans-serif" }}
      dir="ltr"
    >
      <div className="max-w-3xl mx-auto">
        <h1 className="text-3xl font-black text-white mb-2">Privacy Policy</h1>
        <p className="text-sm text-[rgba(255,255,255,0.4)] mb-10">
          Last updated: {LAST_UPDATED}
        </p>

        <Section title="1. Introduction">
          <p>
            Mom3ntum ("we", "us", "our") operates the website at{" "}
            <a href={SITE_URL} className="underline" style={{ color: "#c8f135" }}>
              {SITE_URL}
            </a>{" "}
            (the "Service"). This Privacy Policy explains how we collect, use,
            disclose, and safeguard your information when you use our Service.
          </p>
          <p>
            By accessing or using the Service, you agree to this Privacy Policy.
            If you do not agree, please do not use the Service.
          </p>
        </Section>

        <Section title="2. Information We Collect">
          <p><strong>a) Information you provide directly:</strong></p>
          <ul className="list-disc list-inside space-y-1 mr-4">
            <li>Name and email address (via account registration)</li>
            <li>Survey responses (event interests, budget preferences, travel destinations, companions, and free-text answers)</li>
            <li>Referral codes you share or redeem</li>
            <li>Task submissions (screenshots or links for engagement tasks)</li>
          </ul>

          <p><strong>b) Information from third-party login providers:</strong></p>
          <ul className="list-disc list-inside space-y-1 mr-4">
            <li>
              When you sign in via Google, Facebook, LinkedIn, Twitter/X, or
              Discord, we receive your name, email address, and profile picture
              as permitted by that provider. We do not receive or store your
              passwords from these providers.
            </li>
          </ul>

          <p><strong>c) Information collected automatically:</strong></p>
          <ul className="list-disc list-inside space-y-1 mr-4">
            <li>Device and browser type, IP address, operating system</li>
            <li>Pages visited, time spent, and click interactions</li>
            <li>UTM parameters and referral source</li>
            <li>Cookies and similar tracking technologies (see Section 7)</li>
          </ul>
        </Section>

        <Section title="3. How We Use Your Information">
          <ul className="list-disc list-inside space-y-1 mr-4">
            <li>Provide, maintain, and improve the Service</li>
            <li>Process your engagement tasks and track points</li>
            <li>Display leaderboards and campaign progress</li>
            <li>Send you relevant notifications about your account and campaigns</li>
            <li>Analyze usage trends to improve user experience</li>
            <li>Prevent fraud and enforce our terms</li>
            <li>Respond to your requests and support inquiries</li>
          </ul>
        </Section>

        <Section title="4. How We Share Your Information">
          <p>We do not sell your personal information. We may share data with:</p>
          <ul className="list-disc list-inside space-y-1 mr-4">
            <li>
              <strong>Service providers:</strong> Auth0 (authentication), Google
              Analytics (analytics), Meta Pixel (conversion tracking), and
              hosting providers that help us operate the Service.
            </li>
            <li>
              <strong>Legal obligations:</strong> If required by law, regulation,
              legal process, or governmental request.
            </li>
            <li>
              <strong>Business transfers:</strong> In connection with a merger,
              acquisition, or sale of assets.
            </li>
          </ul>
          <p>
            Leaderboard rankings (display name and points) are visible to other
            authenticated users of the same campaign.
          </p>
        </Section>

        <Section title="5. Data Retention">
          <p>
            We retain your personal information for as long as your account is
            active or as needed to provide the Service. If you request account
            deletion, we will delete your personal data within 30 days, except
            where retention is required by law.
          </p>
        </Section>

        <Section title="6. Your Rights">
          <p>You have the right to:</p>
          <ul className="list-disc list-inside space-y-1 mr-4">
            <li>Access the personal data we hold about you</li>
            <li>Correct inaccurate information</li>
            <li>Request deletion of your data</li>
            <li>Withdraw consent for data processing</li>
            <li>Export your data in a portable format</li>
          </ul>
          <p>
            To exercise any of these rights, contact us at{" "}
            <a
              href={`mailto:${CONTACT_EMAIL}`}
              className="underline"
              style={{ color: "#c8f135" }}
            >
              {CONTACT_EMAIL}
            </a>{" "}
            or visit our{" "}
            <a href="/data-deletion" className="underline" style={{ color: "#c8f135" }}>
              Data Deletion page
            </a>.
          </p>
        </Section>

        <Section title="7. Cookies and Tracking">
          <p>We use the following cookies and tracking technologies:</p>
          <ul className="list-disc list-inside space-y-1 mr-4">
            <li>
              <strong>Essential cookies:</strong> Authentication session and
              preferences (required for the Service to function).
            </li>
            <li>
              <strong>Google Analytics (GA4):</strong> Measures page views,
              events, and user engagement to help us improve the Service.
            </li>
            <li>
              <strong>Meta Pixel:</strong> Tracks conversions and signup events
              for advertising measurement.
            </li>
          </ul>
          <p>
            You can disable non-essential cookies through your browser settings.
            Disabling essential cookies may prevent the Service from functioning
            properly.
          </p>
        </Section>

        <Section title="8. Data Security">
          <p>
            We use industry-standard security measures including HTTPS/TLS
            encryption, secure token-based authentication (JWT), and access
            controls to protect your data. However, no method of transmission
            over the Internet is 100% secure, and we cannot guarantee absolute
            security.
          </p>
        </Section>

        <Section title="9. Children's Privacy">
          <p>
            The Service is not intended for children under 16. We do not
            knowingly collect personal information from children. If you believe
            a child has provided us with personal data, please contact us and we
            will delete it.
          </p>
        </Section>

        <Section title="10. International Data Transfers">
          <p>
            Your information may be transferred to and processed in countries
            other than your own. We ensure appropriate safeguards are in place
            for such transfers in accordance with applicable data protection
            laws.
          </p>
        </Section>

        <Section title="11. Changes to This Policy">
          <p>
            We may update this Privacy Policy from time to time. Changes will be
            posted on this page with an updated "Last updated" date. Continued
            use of the Service after changes constitutes acceptance of the
            revised policy.
          </p>
        </Section>

        <Section title="12. Contact Us">
          <p>
            If you have questions about this Privacy Policy or your personal
            data, contact us at:
          </p>
          <p>
            <strong>Email:</strong>{" "}
            <a
              href={`mailto:${CONTACT_EMAIL}`}
              className="underline"
              style={{ color: "#c8f135" }}
            >
              {CONTACT_EMAIL}
            </a>
          </p>
        </Section>

        <div
          className="mt-12 pt-6 text-center text-xs"
          style={{ borderTop: "1px solid rgba(255,255,255,0.1)", color: "rgba(255,255,255,0.25)" }}
        >
          Mom3ntum &copy; {new Date().getFullYear()}
        </div>
      </div>
    </div>
  );
}
